<template>
   <div :class="['media-card-action']">
      <template v-if="savable">
         <IconMinus v-if="isSaved" />
         <IconAdd v-else />
         <div class="dropdown">
            <ul>
               <li role="button" @click="handleSave">{{ SaveText }}</li>
               <li role="button" @click="toggleAssignClassModal">
                  {{ isAssigned ? "Unassign from Class" : "Assign to Class" }}
               </li>
            </ul>
         </div>
      </template>
      <!-- IF class ID is present because this card is on shown on a classroom -->
      <template v-else-if="classId">
         <IconMinus />
         <div class="dropdown">
            <ul>
               <li role="button" @click="handleUnassign">
                  Unassign from Class
               </li>
            </ul>
         </div>
      </template>
      <AssignClassModal
         :open="openAssignClassModal"
         targetType="media"
         :mediaType="card.type"
         :targetId="card.id"
         @close="toggleAssignClassModal"
      />
   </div>
</template>

<script setup lang="ts">
import toastMessages from "~/data/content/toast-messages.json";
import IconMinus from "~/assets/images/icons/circle-minus.svg?component";
import IconAdd from "~/assets/images/icons/circle-plus.svg?component";
const { assignToClass } = useClassesStore();
const { attachMedia, profile } = useProfile();
const { addToast } = useToast();
const classesStore = useClassesStore();
const { activeClasses } = storeToRefs(classesStore);
// REFS/PROPS ================
const props = defineProps({
   card: { type: Object, required: true },
   savable: { type: Boolean, required: false, default: false },
   classId: { type: Number, required: false },
});
const openAssignClassModal = ref(false);

// COMPUTED ================
const isSaved = computed(() => {
   if (props.card.type == "audio") {
      return profile.value?.media?.audio.some((m) => m.id == props.card.id);
   }
   if (props.card.type == "wistia") {
      return profile.value?.media?.videos.some((m) => m.id == props.card.id);
   }
   return false;
});

const isAssigned = computed(() => {
   if (!activeClasses.value) return false;
   if (props.card.type == "audio") {
      return activeClasses.value?.some((cls) =>
         cls?.media?.audio.some((mod) => mod.id === props.card.id)
      );
   }
   if (props.card.type == "wistia") {
      return activeClasses.value?.some((cls) =>
         cls?.media?.videos.some((mod) => mod.id === props.card.id)
      );
   }
   return false;
});

const SaveText = computed(() => {
   return !isSaved.value ? "Save to Profile" : "Unsave from Profile";
});

// METHODS ================
const handleSave = async () => {
   const method = isSaved.value ? "DELETE" : "POST";
   const title = props.card?.name ?? props.card?.title;
   await attachMedia(props.card.id, props.card.type, method)
      .then(() => {
         const toastMessage =
            method === "DELETE"
               ? { ...toastMessages.removeFromProfile.success }
               : { ...toastMessages.addToProfile.success };
         toastMessage.message = toastMessage.message.replace(/%s/, title);

         addToast(toastMessage);
      })
      .catch(() => {
         const toastMessage =
            method === "DELETE"
               ? { ...toastMessages.removeFromProfile.error }
               : { ...toastMessages.addToProfile.error };
         toastMessage.message = toastMessage.message.replace(/%s/, title);

         addToast(toastMessage);
      });
};

const handleUnassign = async () => {
   const title = props.card?.name ?? props.card?.title;

   await assignToClass(
      "media",
      props.card.id,
      props.classId,
      "DELETE",
      props.card.type
   )
      .then(() => {
         const toastMessage = { ...toastMessages.removeFromClass.success };
         toastMessage.message = toastMessage.message.replace(/%s/, title);
         addToast(toastMessage);
      })
      .catch(() => {
         const toastMessage = { ...toastMessages.removeFromClass.error };
         toastMessage.message = toastMessage.message.replace(/%s/, title);
         addToast(toastMessage);
      });
};

const toggleAssignClassModal = () => {
   openAssignClassModal.value = !openAssignClassModal.value;
};
</script>

<style lang="scss">
div.media-card-action {
   cursor: pointer;
   position: relative;
   svg path {
      stroke: $beige-dk;
      transition: stroke 0.2s ease;
   }

   div.dropdown {
      position: absolute;
      top: 100%;
      right: 0;
      padding: pad(1) 0;
      pointer-events: none;
      opacity: 0;
      background-color: $white;
      border: 1px solid $border-dk;
      border-radius: 4px;
      z-index: 5;
      width: 180px;
      transition: opacity 0.2s ease;
      ul li {
         white-space: nowrap;
         padding: pad(1.5) pad(1.5);
         color: $copy;
         margin-bottom: 0;
         &:hover {
            background-color: $hover-background-lt;
         }
      }
   }
   &:hover {
      div.dropdown {
         opacity: 1;
         pointer-events: all;
      }
      svg path {
         stroke: $copy;
      }
   }
}
</style>
